import React, { useEffect, useRef, useState } from 'react';
import {
  CssBaseline,
  Box,
  Button,
  LinearProgress,
  Typography,
} from '@mui/material';
import { gsap } from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';
import Header from './Header';
import { styled, Theme, useMediaQuery } from '@mui/system';
import CallIcon from '@mui/icons-material/Call';
import { Helmet } from 'react-helmet-async';
import Lenis from 'lenis';
import Banner from './Banner';
import About from './About';
import Gallery from './Gallery';
import MasterLayout from './MasterLayout';
import FloorPlans from './FloorPlans';
import Amenities from './Amenities';
import anime from 'animejs/lib/anime.es.js';
import ConnectedLivingSection from './ConnectedLivingSection';
import Footer from './Footer';
import BottomNav from './BottomNav';
import LeadModal from './LeadModal';
import Banner2 from './Banner2';
import AboutSectionGallery from './AboutSectionGallery';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getProjectDetails } from '../../../../../api/services';
import { project } from '../ProjectInterface';
import FloorPlansNew from './FloorPlansNew';
import About3 from './About3';
import ConnectedLivingSection3 from './ConnectedLivingSection3';
import FloorPlans3 from './FloorPlans3';
import PromenadeFloorPlans from './PromenadeFloorPlans';
import AboutLatest from './AboutLatest';
import VillaVerdeFloorPlans from './VillaVerdeFloorPlans';
import CascadesFloorPlans from './CascadesFloorPlans';
import MiraiMistFloorPlans from './MiraiMistFloorPlans';

gsap.registerPlugin(ScrollToPlugin);

const ButtonContainer = styled(Box)(({ theme }) => ({
  position: 'fixed',
  right: '22px',
  top: '30%',
  transform: 'translateY(-50%) rotate(-90deg)',
  transformOrigin: 'right center',
  zIndex: 1000,
  [theme.breakpoints.down('sm')]: {
    right: '20px',
    top: '30%',
  },
}));

const AnimatedButton = styled(Button)({
  bgcolor: '#9f8054',
  color: '#fff',
  fontWeight: 'bold',
  fontSize: '14px',
  letterSpacing: '1px',
  padding: '15px 20px',
  borderRadius: '10px 10px 0px 0px',
  boxShadow: '0px 4px 15px rgba(159, 128, 84, 0.5)',
  textTransform: 'uppercase',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  zIndex: 100000,
  border: '1.5px solid #ffffff',
  transition: 'transform 0.3s ease, box-shadow 0.3s',
  '&:hover': {
    bgcolor: '#7f6540',
    transform: 'scale(1.05)',
    boxShadow: '0px 8px 25px rgba(159, 128, 84, 0.8)',
  },
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 10px rgba(159, 128, 84, 0.5)' },
    '50%': { boxShadow: '0 0 25px rgba(159, 128, 84, 0.8)' },
    '100%': { boxShadow: '0 0 10px rgba(159, 128, 84, 0.5)' },
  },
});

const sections = [
  'Home',
  'AboutUs',
  'Layout',
  'FloorPlans',
  'Amenities',
  'Location',
  'Contact',
];

const ProjectsPage: React.FC = () => {
  const [index, setIndex] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );
  const sectionsRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeSection, setActiveSection] = useState<string>(sections[0]);
  const { slug } = useParams<{ slug: string }>();

  // All hooks must be called before any return!
  

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);
  }, []);

  useEffect(() => {
    gsap.to(window, { duration: 0, scrollTo: 0 });
    anime({
      targets: '.section h1',
      opacity: 0,
    });
  }, []);

  const handleScroll = () => {
    if (!sectionsRef.current) return;
    const sectionElements = Array.from(
      sectionsRef.current.children,
    ) as HTMLElement[];
    let foundSection = sections[0];
    for (let i = 0; i < sectionElements.length; i++) {
      const rect = sectionElements[i].getBoundingClientRect();
      if (
        rect.top <= window.innerHeight / 2 &&
        rect.bottom > window.innerHeight / 4
      ) {
        foundSection = sectionElements[i].id;
        break;
      }
    }
    if (foundSection !== activeSection) {
      setActiveSection(foundSection);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [activeSection]);

  const {
    data: projectData,
    isLoading,
    error,
  } = useQuery<project>({
    queryKey: ['getProjectDetails', slug],
    queryFn: async () => {
      const response = await getProjectDetails(slug!);
      return response.data;
    },
    enabled: !!slug,
  });
  // Loading and error states must come after all hooks
  if (isLoading) return <LinearProgress />;
  if (error || !projectData)
    return <Typography> </Typography>;

  // Now you can safely use projectData
  const isVibrantDevelopers = slug?.toLowerCase().includes('vibrant');
  const isCascades = slug?.toLowerCase().includes('cascades');

  // Add detection for Promenade (use title if available, fallback to slug)
  const isPromenade =
    (projectData?.projectdetails?.title || "")
      .toString()
      .toLowerCase()
      .includes("promenade") || slug?.toLowerCase().includes("promenade");

  // --- NEW: detect Villa Verde project (by slug or title) ---
  const titleLower = (projectData?.projectdetails?.title || "").toString().toLowerCase();
  const showVillaVerde =
    slug?.toLowerCase().includes("villaverde") ||
    titleLower.includes("villaverde") ||
    titleLower.includes("villa verde");

  // Detect Mirai Mist project
  const isMiraiMist = slug?.toLowerCase().includes('mirai') || titleLower.includes('mirai');

  return (
    <>
      <div ref={sectionsRef}>
        {/* <Header activeSection={activeSection} setActiveSection={setActiveSection} /> */}

        <Header
          activeSection={activeSection}
          setActiveSection={setActiveSection}
          onOpenModal={() => setIsOpen(true)}
          projectSlug={slug}
          projectTitle={projectData.projectdetails?.title}
        />

        <LeadModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          projectSlug={slug || ''}
          projectTitle={projectData.projectdetails?.title || 'MEGALILEO'}
        />

        <Box
          component="section"
          id="Home"
          className="section"
          sx={{ minHeight: isMobile ? 'auto' : 'auto' }}
        >
          <Banner
            herosection={projectData.herosection}
            projectdetails={projectData.projectdetails}
          />
        </Box>
        <Box>
          {/* <AboutL atest/>  */}
        </Box>
        {/* ...other sections... */}
        {isVibrantDevelopers ? (
          <Box
            component="section"
            id="AboutUs"
            className="section"
            sx={{ minHeight: isMobile ? 'auto' : 'auto' }}
          >
            <About3 aboutsection={projectData.aboutsection} />
          </Box>
        ) : isMiraiMist ? (
          <Box
            component="section"
            id="AboutUs"
            className="section"
            sx={{ minHeight: isMobile ? 'auto' : 'auto' }}
          >
            <AboutSectionGallery aboutsection={projectData.aboutsection} />
          </Box>
        ) : (
          <>
            <Box
              component="section"
              id="AboutUs"
              className="section"
              sx={{ minHeight: isMobile ? 'auto' : 'auto' }}
            >
              <AboutSectionGallery aboutsection={projectData.aboutsection} />
            </Box>
            <Box
              component="section"
              id="AboutUs"
              className="section"
              sx={{ minHeight: isMobile ? 'auto' : 'auto' }}
            >
              <Gallery gallerysection={projectData.gallerysection} />
            </Box>
          </>
        )}
        <Box
          component="section"
          id="Layout"
          className="section"
          sx={{ minHeight: isMobile ? 'auto' : 'auto' }}
        >
          <MasterLayout layout={projectData.layout} />
        </Box>
        <Box
          component="section"
          id="FloorPlans"
          className="section"
          sx={{ minHeight: isMobile ? 'auto' : 'auto' }}
        >
          {isPromenade ? (
            // Show the dedicated Promenade component for Promenade projects
            <PromenadeFloorPlans title="PROMENADE FLOOR PLANS" />
          ) : showVillaVerde ? (
            // Only render VillaVerdeFloorPlans for Villa Verde project
            <VillaVerdeFloorPlans title={projectData.projectdetails?.title} />
          ) : isVibrantDevelopers ? (
            <FloorPlans3 floorplans={projectData.floorplans} />
          ) :  isCascades ? (
            <CascadesFloorPlans title={projectData.projectdetails?.title} />          ) : isMiraiMist ? (
            <MiraiMistFloorPlans title={projectData.projectdetails?.title} />          ) : (
            <FloorPlansNew floorplans={projectData.floorplans} />
          )}
        </Box>

        <Box
          component="section"
          id="Amenities"
          className="section"
          sx={{ minHeight: isMobile ? 'auto' : 'auto' }}
        >
          <Amenities amenities={projectData.amenities} />
        </Box>

        {/* --- RENDER VillaVerdeFloorPlans ONLY for Villa Verde project --- */}
        

        <Box
          component="section"
          id="Location"
          className="section"
          sx={{ minHeight: 'auto' }}
        >
           <ConnectedLivingSection
              connectedliving={projectData.connectedliving}
              projectTitle={projectData.projectdetails?.title}
            />
          {/* {isVibrantDevelopers ? (
            <ConnectedLivingSection3
              connectedliving={projectData.connectedliving}
              projectTitle={projectData.projectdetails?.title}
            />
          ) : (
            <ConnectedLivingSection
              connectedliving={projectData.connectedliving}
              projectTitle={projectData.projectdetails?.title}
            />
          )} */}
        </Box>
        <Box
          component="section"
          id="Contact"
          className="section"
          sx={{ minHeight: 'auto' }}
        >
          <Footer
            projectTitle={projectData.projectdetails?.title}
            projectSlug={slug}
          />
        </Box>
        {!isMobile ? (
          <ButtonContainer>
            <AnimatedButton
              ref={buttonRef}
              variant="contained"
              onClick={() => setIsOpen(true)}
            >
              <CallIcon />
              Enquire  Now
            </AnimatedButton>
          </ButtonContainer>
        ) : (
          <BottomNav />
        )}
        {/* <LeadModal isOpen={isOpen} onClose={() => setIsOpen(false)} projectTitle={projectData.projectdetails?.title} /> */}
        <LeadModal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          projectSlug={slug || ''}
          projectTitle={projectData.projectdetails?.title }
        />
      </div>
    </>
  );
};

export default ProjectsPage;
